<template>
  <div class="compliance-trends">
    <div class="ct-head">
      <div>
        <h3 class="ct-title">Compliance over time</h3>
        <p class="ct-sub">
          Team logging, compliance and hydration trends from the nightly
          snapshots.
        </p>
      </div>
      <div class="ct-range">
        <button v-for="d in ranges" :key="d" class="ct-range-btn"
          :class="{ active: days === d }" @click="setDays(d)">
          {{ d }}d
        </button>
      </div>
    </div>

    <p v-if="error" class="ct-error">{{ error }}</p>
    <div v-if="loading" class="ct-msg">Loading trends…</div>

    <div v-else-if="!points.length" class="ct-msg">
      No trend data yet — snapshots are written nightly, so this fills in over
      the coming days.
    </div>

    <template v-else>
      <!-- Latest + change vs start of range -->
      <div class="ct-cards">
        <div v-for="m in metrics" :key="m.key" class="ct-card">
          <span class="ct-dot" :style="{ background: m.color }"></span>
          <span class="ct-card-label">{{ m.label }}</span>
          <span class="ct-card-value">{{ latest(m.key) }}%</span>
          <span class="ct-card-delta" :class="deltaClass(m.key)">
            {{ deltaLabel(m.key) }}
          </span>
        </div>
      </div>

      <!-- SVG line chart -->
      <div class="ct-chart-wrap">
        <svg :viewBox="`0 0 ${W} ${H}`" class="ct-chart" preserveAspectRatio="none">
          <!-- gridlines at 0/25/50/75/100% -->
          <g>
            <line v-for="g in [0, 25, 50, 75, 100]" :key="g"
              :x1="padL" :x2="W - padR" :y1="y(g)" :y2="y(g)" class="ct-grid" />
          </g>
          <!-- match-day markers -->
          <line v-for="mx in matchXs" :key="'m' + mx"
            :x1="mx" :x2="mx" :y1="padT" :y2="H - padB" class="ct-match" />
          <!-- metric lines -->
          <polyline v-for="m in metrics" :key="m.key" :points="line(m.key)"
            :stroke="m.color" class="ct-line" />
        </svg>
        <div class="ct-axis">
          <span>{{ firstDate }}</span>
          <span v-if="points.some(p => p.has_match)" class="ct-legend-match">| match day</span>
          <span>{{ lastDate }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { Nutrition } from "@/services/nutrition-service";

export default {
  name: "ComplianceTrends",
  props: {
    clubId: { type: [String, Number], default: null },
    teamId: { type: [String, Number], default: null },
  },
  data() {
    return {
      points: [],
      loading: false,
      error: "",
      days: 30,
      ranges: [14, 30, 90],
      // Chart geometry
      W: 720,
      H: 220,
      padL: 34,
      padR: 12,
      padT: 12,
      padB: 22,
      metrics: [
        { key: "compliance_rate", label: "Compliance", color: "#2b8a3e" },
        { key: "hydration_rate", label: "Hydration", color: "#1c7ed6" },
        { key: "logging_rate", label: "Logging", color: "#e8590c" },
      ],
    };
  },
  computed: {
    firstDate() {
      return this.points.length ? this.fmt(this.points[0].snapshot_date) : "";
    },
    lastDate() {
      return this.points.length
        ? this.fmt(this.points[this.points.length - 1].snapshot_date)
        : "";
    },
    matchXs() {
      return this.points
        .map((p, i) => (p.has_match ? this.x(i) : null))
        .filter((v) => v !== null);
    },
  },
  watch: {
    clubId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      if (!this.clubId) return;
      this.loading = true;
      this.error = "";
      try {
        const res = await Nutrition.getComplianceTrends(
          this.clubId,
          this.teamId,
          this.days
        );
        const payload = res.data ?? res;
        if (payload && payload.success) {
          this.points = (payload.data || []).map((r) => ({
            ...r,
            compliance_rate: Number(r.compliance_rate) || 0,
            hydration_rate: Number(r.hydration_rate) || 0,
            logging_rate: Number(r.logging_rate) || 0,
            has_match: Number(r.has_match) === 1,
          }));
        } else {
          this.error = (payload && payload.error) || "Could not load trends.";
        }
      } catch (e) {
        this.error = "Could not load trends.";
      } finally {
        this.loading = false;
      }
    },
    setDays(d) {
      this.days = d;
      this.load();
    },
    // --- chart helpers ---
    x(i) {
      const n = this.points.length;
      if (n <= 1) return this.padL;
      const usable = this.W - this.padL - this.padR;
      return this.padL + (usable * i) / (n - 1);
    },
    y(pct) {
      const usable = this.H - this.padT - this.padB;
      return this.padT + usable * (1 - Math.max(0, Math.min(100, pct)) / 100);
    },
    line(key) {
      return this.points.map((p, i) => `${this.x(i)},${this.y(p[key])}`).join(" ");
    },
    latest(key) {
      return this.points.length ? this.points[this.points.length - 1][key] : 0;
    },
    delta(key) {
      if (this.points.length < 2) return 0;
      return (
        this.points[this.points.length - 1][key] - this.points[0][key]
      );
    },
    deltaLabel(key) {
      const d = Math.round(this.delta(key));
      return `${d > 0 ? "▲ +" : d < 0 ? "▼ " : ""}${d === 0 ? "—" : d}${d !== 0 ? " pts" : ""}`;
    },
    deltaClass(key) {
      const d = this.delta(key);
      return d > 0 ? "up" : d < 0 ? "down" : "flat";
    },
    fmt(dateStr) {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    },
  },
};
</script>

<style scoped>
.compliance-trends {
  background: #fff;
  border: 1px solid #edf0f2;
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 18px;
}
.ct-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}
.ct-title {
  margin: 0 0 3px;
  font-size: 1.1rem;
  color: #1a1a1a;
}
.ct-sub {
  margin: 0;
  font-size: 0.84rem;
  color: #6b7278;
  max-width: 440px;
}
.ct-range {
  display: flex;
  gap: 6px;
}
.ct-range-btn {
  border: 1px solid #d5d9dd;
  background: #fff;
  color: #555;
  border-radius: 7px;
  padding: 5px 11px;
  font-size: 0.82rem;
  cursor: pointer;
}
.ct-range-btn.active {
  background: var(--brand-color, #2b61d6);
  border-color: var(--brand-color, #2b61d6);
  color: #fff;
}
.ct-error {
  color: #c0392b;
  font-size: 0.86rem;
}
.ct-msg {
  color: #7a7f84;
  padding: 26px 0;
  text-align: center;
  font-size: 0.9rem;
}
.ct-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 16px 0 8px;
}
.ct-card {
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 2px 8px;
  background: #f8f9fa;
  border: 1px solid #edf0f2;
  border-radius: 10px;
  padding: 10px 14px;
  min-width: 140px;
}
.ct-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  grid-row: span 2;
}
.ct-card-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7278;
}
.ct-card-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a1a;
  grid-column: 2;
}
.ct-card-delta {
  grid-column: 2;
  font-size: 0.74rem;
  font-weight: 600;
}
.ct-card-delta.up {
  color: #2b8a3e;
}
.ct-card-delta.down {
  color: #c0392b;
}
.ct-card-delta.flat {
  color: #868c92;
}
.ct-chart-wrap {
  margin-top: 8px;
}
.ct-chart {
  width: 100%;
  height: 220px;
  display: block;
}
.ct-grid {
  stroke: #eef1f3;
  stroke-width: 1;
}
.ct-match {
  stroke: #f1d0a6;
  stroke-width: 1;
  stroke-dasharray: 3 3;
}
.ct-line {
  fill: none;
  stroke-width: 2;
  vector-effect: non-scaling-stroke;
  stroke-linejoin: round;
  stroke-linecap: round;
}
.ct-axis {
  display: flex;
  justify-content: space-between;
  font-size: 0.74rem;
  color: #8a9096;
  margin-top: 2px;
}
.ct-legend-match {
  color: #b5852a;
}
</style>
